import { useEffect, useRef, useState } from "react";
import { useWarehouses } from "../lib/useWarehouses";
import { useTranslation } from "react-i18next";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Search, X, ArrowUp, ArrowDown, Lock } from "lucide-react";
import { api } from "../lib/api";

// iter 214 (2026-02): 下拉日历式日期选择器。取代原生 <input type="date">。
// 值仍以 "YYYY-MM-DD" ISO 字符串存到 filters,后端接口不变。
// yyyy-mm when the range is exactly one whole calendar month, else "".
function monthValue(from, to) {
  if (!from || !to || from.slice(0, 7) !== to.slice(0, 7) || !from.endsWith("-01")) return "";
  const [y, m] = from.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return to.endsWith(`-${String(last).padStart(2, "0")}`) ? from.slice(0, 7) : "";
}

// iter 256: native browser date input (month/year dropdown, Clear / Today) per user request.
function DatePickerField({ value, onChange, readOnly, placeholder, testId, lockedBg }) {
  return (
    <input
      type="date"
      data-testid={testId}
      value={value || ""}
      disabled={readOnly}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`w-36 h-7 px-2 rounded-md border border-gray-300 font-mono text-xs hover:border-brand-300 focus:outline-none focus:ring-1 focus:ring-brand-500 ${readOnly ? (lockedBg || "bg-gray-50 text-gray-500") : "bg-white text-gray-700"}`}
    />
  );
}

// Reusable async autocomplete for free-text filters that fetch distinct values
// from the backend. Debounces the query and closes on outside click.
function AsyncAutocompleteField({ value, onChange, endpoint, placeholder, testId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const skipRef = useRef(false);

  useEffect(() => {
    if (skipRef.current) { skipRef.current = false; return; }
    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(endpoint, { params: { q: value || undefined, limit: 15 }, signal: controller.signal });
        setSuggestions(data.items || []);
      } catch { /* aborted or errored — leave suggestions as-is */ }
    }, 120);
    return () => { clearTimeout(t); controller.abort(); };
  }, [value, endpoint]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (v) => { skipRef.current = true; onChange(v); setOpen(false); setHighlight(-1); };
  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(suggestions[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        data-testid={`${testId}-input`}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        className="rounded-md border-gray-300 h-7 text-xs focus-visible:ring-brand-500"
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div data-testid={`${testId}-suggestions`} className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {suggestions.map((name, idx) => (
            <button
              key={name}
              type="button"
              data-testid={`${testId}-option-${idx}`}
              onMouseDown={(e) => { e.preventDefault(); pick(name); }}
              onMouseEnter={() => setHighlight(idx)}
              className={`w-full text-left px-3 py-2 text-xs truncate ${idx === highlight ? "bg-brand-50 text-brand-700" : "text-gray-700 hover:bg-gray-50"}`}
              title={name}
            >{name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomerAutocomplete({ value, onChange, matchOp, onMatchOpChange, disabled }) {
  return (
    <div className={`relative ${disabled ? "opacity-70 pointer-events-none" : ""}`}>
      <AsyncAutocompleteField value={value} onChange={onChange} endpoint="/inventory/customer-suggestions" placeholder={matchOp === "starts_with" ? "starts with…" : "contains…"} testId="filter-customer" />
      <button
        type="button"
        data-testid="filter-customer-match-toggle"
        onClick={() => onMatchOpChange(matchOp === "starts_with" ? "contains" : "starts_with")}
        title={matchOp === "starts_with" ? "Match: starts with (click to switch to contains)" : "Match: contains (click to switch to starts with)"}
        className={`absolute right-1.5 top-1/2 -translate-y-1/2 h-6 px-1.5 rounded text-[10px] font-mono font-semibold border transition-colors ${matchOp === "starts_with" ? "bg-brand-50 border-brand-300 text-brand-700" : "bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300"}`}
      >
        {matchOp === "starts_with" ? "a…" : "…a…"}
      </button>
    </div>
  );
}

// Filter section keys shared between FilterBar visibility control and the
// per-View "visible_filters" config saved on the backend.
// iter 209 (2026-02): "customer" filter removed per user request — 产品清单
// 不需要按客户过滤 (客户过滤已由报价单侧承担)。
export const ALL_FILTER_KEYS = ["keyword", "budget_no", "factory_name", "purchaser", "external_sales", "contract_no", "invoice_no", "tag", "category", "product_type", "approval", "qty", "shipping_qty", "ci_qty", "has_samples", "sample_warehouse", "date"];
export const FILTER_LABELS = {
  keyword: "Keyword Search",
  budget_no: "Budget No",
  factory_name: "Factory",
  purchaser: "Purchaser",
  external_sales: "External Sales",
  contract_no: "Contract No",
  invoice_no: "Invoice No",
  tag: "Tag",
  category: "Category",
  product_type: "Product Type",
  approval: "Approval",
  qty: "Quantity Range",
  shipping_qty: "Shipping Qty Range",
  ci_qty: "CI Inventory Qty Range",
  has_samples: "Has Sample Stock",
  sample_warehouse: "Sample Warehouse",
  date: "Date Type + Range",
};

export default function FilterBar({
  filters, setFilters, onReset,
  view, sortDir, setSortDir, categoryOptions = [],
  visibleFilters, lockedFields = [],
  qFields,
  warehouseScope = null,
}) {
  const { t } = useTranslation();
  const { warehouses } = useWarehouses();
  const update = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  // iter 176: when user starts a keyword search (transition from empty → any
  // non-empty value), wipe every other filter so the keyword is the sole
  // narrowing criterion. Once q is non-empty, editing further keystrokes
  // doesn't re-wipe, so users can still add filters after typing.
  const updateKeyword = (value) => setFilters((f) => {
    const wasEmpty = !((f.q || "").length);
    const willBeNonEmpty = !!(value || "").length;
    if (wasEmpty && willBeNonEmpty) {
      // Reset everything except modifier keys that aren't actual filters,
      // and preserve the sort field default.
      return {
        ...f,
        q: value,
        min_qty: "", max_qty: "",
        min_shipping_qty: "", max_shipping_qty: "",
        min_ci_qty: "", max_ci_qty: "", has_samples: false, sample_warehouse_id: "",
        category: "", product_type: "",
        customer_name: "",
        budget_no: "",
        factory_name: "",
        purchaser: "",
        external_sales: "",
        contract_no: "",
        invoice_no: "",
        tag: "",
        approval: "",
        date_from: "", date_to: "",
      };
    }
    return { ...f, q: value };
  });
  const isVisible = (k) => !visibleFilters || visibleFilters.length === 0 || visibleFilters.includes(k);
  const isLocked = (k) => Array.isArray(lockedFields) && lockedFields.includes(k);
  // iter 87: shared helper wrapping each locked field with a lock badge; the
  // input inside is expected to already have disabled/readOnly applied by the caller.
  const LockBadge = () => (
    <span data-testid="filter-lock-badge" title="Locked by view preset" className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-gray-400 uppercase font-mono">
      <Lock size={10} strokeWidth={2} /> locked
    </span>
  );

  // iter 246 (2026-02): Placeholder / Q_FIELD_SHORT 已移除 —— 用户反馈
  // 不需要提示字段短名。语法徽章 (Space=AND · Comma=OR) 在 label 旁
  // 已足够说明多值语义。

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-2.5" data-testid="filter-panel">
      {/* iter 206 (2026-02): 内边距 p-6 → p-2.5, 列间距 gap-4 → gap-2, 输入
          h-9 → h-7, 字号统一收紧, 跟顶部工具条对齐 (~50%). */}

      <div className="flex flex-wrap items-end gap-2">
        {isVisible("keyword") && (
        <div className="relative w-64">
          {/* iter 245 (2026-02): label 改为"全字段搜索",加个小 hint 徽章
              提示 "空格=AND · 逗号=OR" 的多值语义;宽度 w-52 → w-64 让
              新语法能舒展开。 */}
          <div className="flex items-center gap-1.5 mb-0.5">
            <label className="text-[11px] font-semibold text-gray-700">{t("dashboard.filters.keyword_search")}{isLocked("q") && <LockBadge />}</label>
            <span
              className="text-[9px] font-medium text-brand-700 bg-brand-50 border border-brand-100 rounded px-1 py-[1px] leading-none select-none"
              title={t("dashboard.filters.keyword_search_hint")}
              data-testid="filter-keyword-syntax-hint"
            >
              {t("dashboard.filters.keyword_search_hint")}
            </span>
          </div>
          <Search size={14} strokeWidth={1.75} className="absolute left-3 top-[38px] text-gray-400" />
          <Input
            data-testid="filter-keyword-input"
            value={filters.q}
            onChange={(e) => updateKeyword(e.target.value)}
            readOnly={isLocked("q")}
            className={`rounded-md border-gray-300 pl-8 h-7 text-xs focus-visible:ring-brand-500 ${isLocked("q") ? "bg-gray-50 text-gray-500" : ""}`}
          />
        </div>
        )}

        {/* iter 209: Customer filter block removed per user request. */}

        {isVisible("budget_no") && (
        <div className="w-36">
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.budget_no")}{isLocked("budget_no") && <LockBadge />}</label>
          <Input
            data-testid="filter-budget-no-input"
            value={filters.budget_no || ""}
            onChange={(e) => update("budget_no", e.target.value)}
            readOnly={isLocked("budget_no")}
            placeholder="e.g. E27"
            className={`rounded-md border-gray-300 h-7 text-xs focus-visible:ring-brand-500 ${isLocked("budget_no") ? "bg-gray-50 text-gray-500" : ""}`}
          />
        </div>
        )}

        {isVisible("factory_name") && (
        <div className="w-44">
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.factory")}{isLocked("factory_name") && <LockBadge />}</label>
          <div className={isLocked("factory_name") ? "opacity-70 pointer-events-none" : ""}>
            <AsyncAutocompleteField
              value={filters.factory_name || ""}
              onChange={(v) => update("factory_name", v)}
              endpoint="/inventory/factory-suggestions"
              placeholder="e.g. Ding Ding"
              testId="filter-factory-name"
            />
          </div>
        </div>
        )}

        {isVisible("purchaser") && (
        <div className="w-32">
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.purchaser")}{isLocked("purchaser") && <LockBadge />}</label>
          <Input
            data-testid="filter-purchaser"
            value={filters.purchaser || ""}
            onChange={(e) => update("purchaser", e.target.value)}
            readOnly={isLocked("purchaser")}
            placeholder="e.g. Tim"
            className={`rounded-md border-gray-300 h-7 text-xs focus-visible:ring-brand-500 ${isLocked("purchaser") ? "bg-gray-50 text-gray-500" : ""}`}
          />
        </div>
        )}

        {isVisible("external_sales") && (
        <div className="w-32">
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.external_sales")}{isLocked("external_sales") && <LockBadge />}</label>
          <Input
            data-testid="filter-external-sales"
            value={filters.external_sales || ""}
            onChange={(e) => update("external_sales", e.target.value)}
            readOnly={isLocked("external_sales")}
            placeholder="e.g. fancy"
            className={`rounded-md border-gray-300 h-7 text-xs focus-visible:ring-brand-500 ${isLocked("external_sales") ? "bg-gray-50 text-gray-500" : ""}`}
          />
        </div>
        )}

        {isVisible("contract_no") && (
        <div className="w-36">
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.contract_no")}{isLocked("contract_no") && <LockBadge />}</label>
          <div className={isLocked("contract_no") ? "opacity-70 pointer-events-none" : ""}>
            <AsyncAutocompleteField
              value={filters.contract_no || ""}
              onChange={(v) => update("contract_no", v)}
              endpoint="/inventory/contract-no-suggestions"
              placeholder="e.g. 9F1718"
              testId="filter-contract-no"
            />
          </div>
        </div>
        )}

        {isVisible("invoice_no") && (
        <div className="w-36">
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.invoice_no")}{isLocked("invoice_no") && <LockBadge />}</label>
          <div className={isLocked("invoice_no") ? "opacity-70 pointer-events-none" : ""}>
            <AsyncAutocompleteField
              value={filters.invoice_no || ""}
              onChange={(v) => update("invoice_no", v)}
              endpoint="/inventory/invoice-no-suggestions"
              placeholder="e.g. CI2026"
              testId="filter-invoice-no"
            />
          </div>
        </div>
        )}

        {isVisible("tag") && (
        <div className="w-32">
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.tag")}{isLocked("tag") && <LockBadge />}</label>
          <div className={isLocked("tag") ? "opacity-70 pointer-events-none" : ""}>
            <AsyncAutocompleteField
              value={filters.tag || ""}
              onChange={(v) => update("tag", v)}
              endpoint="/inventory/tag-suggestions"
              placeholder="e.g. Hot"
              testId="filter-tag"
            />
          </div>
        </div>
        )}

        {isVisible("category") && (
        <div className="w-36">
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.category")}{isLocked("category") && <LockBadge />}</label>
          <Select value={filters.category || "all"} onValueChange={(v) => update("category", v === "all" ? "" : v)} disabled={isLocked("category")}>
            <SelectTrigger data-testid="filter-category-select" className={`rounded-md border-gray-300 h-7 text-xs focus:ring-brand-500 ${isLocked("category") ? "bg-gray-50 text-gray-500" : ""}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-lg">
              <SelectItem value="all">All Types</SelectItem>
              {categoryOptions.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        )}

        {isVisible("product_type") && (
        <div className="w-36">
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.product_type")}{isLocked("product_type") && <LockBadge />}</label>
          <Select value={filters.product_type || "all"} onValueChange={(v) => update("product_type", v === "all" ? "" : v)} disabled={isLocked("product_type")}>
            <SelectTrigger data-testid="filter-product-type-select" className={`rounded-md border-gray-300 h-7 text-xs focus:ring-brand-500 ${isLocked("product_type") ? "bg-gray-50 text-gray-500" : ""}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-lg">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="CI产品">CI Product</SelectItem>
              <SelectItem value="拼箱产品">Consolidated Shipment</SelectItem>
              {/* iter 120: 拼单 marks products whose quote line customer
                  code auto-computes as item_no-contract_no on add-to-cart. */}
              <SelectItem value="拼单">Pooled Order</SelectItem>
            </SelectContent>
          </Select>
        </div>
        )}

        {isVisible("approval") && (
        <div className="w-36">
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.approval")}{isLocked("approval") && <LockBadge />}</label>
          <Select value={filters.approval || "all"} onValueChange={(v) => update("approval", v === "all" ? "" : v)} disabled={isLocked("approval")}>
            <SelectTrigger data-testid="filter-approval-select" className={`rounded-md border-gray-300 h-7 text-xs focus:ring-brand-500 ${isLocked("approval") ? "bg-gray-50 text-gray-500" : ""}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-lg">
              <SelectItem value="all">All</SelectItem>
              <SelectItem data-testid="filter-approval-yes" value="yes">Approved</SelectItem>
              <SelectItem data-testid="filter-approval-no" value="no">Rejected</SelectItem>
              <SelectItem data-testid="filter-approval-empty" value="empty">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
        )}

        {isVisible("qty") && (
        <div>
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.quantity_range")}{(isLocked("min_qty") || isLocked("max_qty")) && <LockBadge />}</label>
          <div className="flex items-center gap-1.5">
            <Input data-testid="filter-qty-min" type="number" value={filters.min_qty} onChange={(e) => update("min_qty", e.target.value)} readOnly={isLocked("min_qty")} placeholder="Min" className={`w-14 rounded-md border-gray-300 h-7 font-mono text-xs focus-visible:ring-brand-500 ${isLocked("min_qty") ? "bg-gray-50 text-gray-500" : ""}`} />
            <span className="text-gray-300 text-xs">–</span>
            <Input data-testid="filter-qty-max" type="number" value={filters.max_qty} onChange={(e) => update("max_qty", e.target.value)} readOnly={isLocked("max_qty")} placeholder="Max" className={`w-14 rounded-md border-gray-300 h-7 font-mono text-xs focus-visible:ring-brand-500 ${isLocked("max_qty") ? "bg-gray-50 text-gray-500" : ""}`} />
          </div>
        </div>
        )}

        {/* iter 213 (2026-02): 出货数量范围过滤 (variant.shipping_qty)。 */}
        {isVisible("shipping_qty") && (
        <div>
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.shipping_qty_range")}{(isLocked("min_shipping_qty") || isLocked("max_shipping_qty")) && <LockBadge />}</label>
          <div className="flex items-center gap-1.5">
            <Input data-testid="filter-shipping-qty-min" type="number" value={filters.min_shipping_qty || ""} onChange={(e) => update("min_shipping_qty", e.target.value)} readOnly={isLocked("min_shipping_qty")} placeholder="Min" className={`w-14 rounded-md border-gray-300 h-7 font-mono text-xs focus-visible:ring-brand-500 ${isLocked("min_shipping_qty") ? "bg-gray-50 text-gray-500" : ""}`} />
            <span className="text-gray-300 text-xs">–</span>
            <Input data-testid="filter-shipping-qty-max" type="number" value={filters.max_shipping_qty || ""} onChange={(e) => update("max_shipping_qty", e.target.value)} readOnly={isLocked("max_shipping_qty")} placeholder="Max" className={`w-14 rounded-md border-gray-300 h-7 font-mono text-xs focus-visible:ring-brand-500 ${isLocked("max_shipping_qty") ? "bg-gray-50 text-gray-500" : ""}`} />
          </div>
        </div>
        )}

        {/* iter 267: 仅显示有样品库存 (product.sample_qty > 0)。 */}
        {isVisible("has_samples") && (
        <div>
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.has_samples")}{isLocked("has_samples") && <LockBadge />}</label>
          <button type="button" role="switch" aria-checked={!!filters.has_samples} disabled={isLocked("has_samples")} data-testid="filter-has-samples-toggle"
            onClick={() => update("has_samples", !filters.has_samples)}
            className={`relative h-7 w-12 rounded-full transition-colors ${filters.has_samples ? "bg-brand-600" : "bg-gray-300"} disabled:opacity-60`}>
            <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${filters.has_samples ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
        )}
        {/* iter 276: 按样品仓库筛选。未选时跟随当前报价单仓库(warehouseScope)。 */}
        {(isVisible("sample_warehouse") || warehouseScope) && (
        <div>
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.sample_warehouse")}</label>
          <select value={filters.sample_warehouse_id || (warehouseScope?.id ?? "")} onChange={(e) => update("sample_warehouse_id", e.target.value)} data-testid="filter-sample-warehouse-select"
            className="h-7 text-xs border border-gray-300 rounded-md px-2 bg-white min-w-[120px]">
            <option value="">{warehouseScope ? t("dashboard.filters.follow_quote") : t("dashboard.filters.all_warehouses")}</option>
            {warehouses.filter((w) => w.active || w.id === filters.sample_warehouse_id).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          {warehouseScope && !filters.sample_warehouse_id && <span className="ml-1 text-[10px] text-gray-400" data-testid="filter-warehouse-scope">{warehouseScope.name}</span>}
        </div>
        )}
        {/* iter 253: CI 库存数量范围过滤 (variant.ci_inventory_qty)。 */}
        {isVisible("ci_qty") && (
        <div>
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.ci_qty_range")}{(isLocked("min_ci_qty") || isLocked("max_ci_qty")) && <LockBadge />}</label>
          <div className="flex items-center gap-1.5">
            <Input data-testid="filter-ci-qty-min" type="number" value={filters.min_ci_qty || ""} onChange={(e) => update("min_ci_qty", e.target.value)} readOnly={isLocked("min_ci_qty")} placeholder="Min" className={`w-14 rounded-md border-gray-300 h-7 font-mono text-xs focus-visible:ring-brand-500 ${isLocked("min_ci_qty") ? "bg-gray-50 text-gray-500" : ""}`} />
            <span className="text-gray-300 text-xs">–</span>
            <Input data-testid="filter-ci-qty-max" type="number" value={filters.max_ci_qty || ""} onChange={(e) => update("max_ci_qty", e.target.value)} readOnly={isLocked("max_ci_qty")} placeholder="Max" className={`w-14 rounded-md border-gray-300 h-7 font-mono text-xs focus-visible:ring-brand-500 ${isLocked("max_ci_qty") ? "bg-gray-50 text-gray-500" : ""}`} />
          </div>
        </div>
        )}

        {isVisible("date") && (
        <>
        <div className="w-32">
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">Date Type{isLocked("date_field") && <LockBadge />}</label>
          <Select value={filters.date_field || "contract_date"} onValueChange={(v) => update("date_field", v)} disabled={isLocked("date_field")}>
            <SelectTrigger data-testid="filter-date-field-select" className={`rounded-md border-gray-300 h-7 text-xs focus:ring-brand-500 ${isLocked("date_field") ? "bg-gray-50 text-gray-500" : ""}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-lg">
              <SelectItem data-testid="filter-date-field-contract" value="contract_date">Contract Date</SelectItem>
              <SelectItem data-testid="filter-date-field-delivery" value="delivery_date">Delivery Date</SelectItem>
              <SelectItem data-testid="filter-date-field-shipping" value="shipping_date">Shipping Date</SelectItem>
              <SelectItem data-testid="filter-date-field-po-shipping" value="po_shipping_date">PO Shipping Date</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* iter 255: 按月份筛选 — 选月份后自动填入该月 1 日 ~ 月末到 Date Range。 */}
        <div className="w-32">
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">{t("dashboard.filters.month")}{(isLocked("date_from") || isLocked("date_to")) && <LockBadge />}</label>
          <input
            type="month"
            data-testid="filter-month-input"
            value={monthValue(filters.date_from, filters.date_to)}
            disabled={isLocked("date_from") || isLocked("date_to")}
            onChange={(e) => {
              const m = e.target.value;
              if (!m) { setFilters((f) => ({ ...f, date_from: "", date_to: "" })); return; }
              const [y, mo] = m.split("-").map(Number);
              const last = new Date(y, mo, 0).getDate();
              setFilters((f) => ({ ...f, date_from: `${m}-01`, date_to: `${m}-${String(last).padStart(2, "0")}` }));
            }}
            className={`w-full h-7 rounded-md border border-gray-300 px-2 text-xs font-mono bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 ${(isLocked("date_from") || isLocked("date_to")) ? "bg-gray-50 text-gray-500" : ""}`}
          />
        </div>

        <div>
          <label className="text-[11px] font-semibold text-gray-700 block mb-0.5">Date Range{(isLocked("date_from") || isLocked("date_to")) && <LockBadge />}</label>
          <div className="flex items-center gap-1.5">
            <DatePickerField
              value={filters.date_from || ""}
              onChange={(v) => update("date_from", v)}
              readOnly={isLocked("date_from")}
              placeholder="From"
              testId="filter-date-from"
            />
            <span className="text-gray-300 text-xs">–</span>
            <DatePickerField
              value={filters.date_to || ""}
              onChange={(v) => update("date_to", v)}
              readOnly={isLocked("date_to")}
              placeholder="To"
              testId="filter-date-to"
            />
          </div>
        </div>
        </>
        )}

        <Button
          data-testid="filter-reset-button"
          onClick={onReset}
          variant="ghost"
          className="h-7 text-[#6B7280] hover:text-[#273142] hover:bg-transparent text-[11px] font-medium"
        >
          <X size={14} strokeWidth={1.75} className="mr-1" /> {t("common.reset")}
        </Button>
      </div>

    </div>
  );
}
